import { z } from 'zod';

export const entityTypeSchema = z.enum([
  'referring_provider',
  'lab',
  'pharmacy',
  'payer',
  'facility',
  'other',
]);

export type EntityType = z.infer<typeof entityTypeSchema>;

export const ENTITY_TYPE_LABELS: Record<EntityType, string> = {
  referring_provider: 'Referring Provider',
  lab: 'Lab',
  pharmacy: 'Pharmacy',
  payer: 'Payer',
  facility: 'Facility',
  other: 'Other',
};

export const entityResponseSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  type: z.string(),
  name: z.string(),
  npi: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  fax: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  addressLine1: z.string().nullable().optional(),
  addressLine2: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  state: z.string().nullable().optional(),
  postalCode: z.string().nullable().optional(),
  metadata: z.record(z.string(), z.unknown()),
  documentCount: z.number(),
  lastInteractionAt: z.string().nullable().optional(),
  rowStatus: z.number(),
  createdBy: z.string().nullable().optional(),
  updatedBy: z.string().nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type EntityResponse = z.infer<typeof entityResponseSchema>;

export const entityContactResponseSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  entityId: z.string().uuid(),
  contactName: z.string().nullable().optional(),
  role: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  fax: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  extension: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  isPrimary: z.boolean(),
  rowStatus: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type EntityContactResponse = z.infer<typeof entityContactResponseSchema>;

export const createEntitySchema = z.object({
  type: z.string().min(1, 'Type is required'),
  name: z.string().min(1, 'Name is required'),
  npi: z.string().optional().nullable(),
  phone: z.string().optional().nullable(),
  fax: z.string().optional().nullable(),
  email: z.string().email().optional().nullable(),
  addressLine1: z.string().optional().nullable(),
  addressLine2: z.string().optional().nullable(),
  city: z.string().optional().nullable(),
  state: z.string().optional().nullable(),
  postalCode: z.string().optional().nullable(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export type CreateEntityRequest = z.infer<typeof createEntitySchema>;

export const updateEntitySchema = z.object({
  type: z.string().min(1).optional(),
  name: z.string().min(1).optional(),
  npi: z.string().optional().nullable(),
  phone: z.string().optional().nullable(),
  fax: z.string().optional().nullable(),
  email: z.string().email().optional().nullable(),
  addressLine1: z.string().optional().nullable(),
  addressLine2: z.string().optional().nullable(),
  city: z.string().optional().nullable(),
  state: z.string().optional().nullable(),
  postalCode: z.string().optional().nullable(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export type UpdateEntityRequest = z.infer<typeof updateEntitySchema>;

export const createEntityContactSchema = z.object({
  contactName: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  phone: z.string().optional().nullable(),
  fax: z.string().optional().nullable(),
  email: z.string().email().optional().nullable(),
  extension: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
  isPrimary: z.boolean().optional(),
});

export type CreateEntityContactRequest = z.infer<typeof createEntityContactSchema>;

export const updateEntityContactSchema = z.object({
  contactName: z.string().optional().nullable(),
  role: z.string().optional().nullable(),
  phone: z.string().optional().nullable(),
  fax: z.string().optional().nullable(),
  email: z.string().email().optional().nullable(),
  extension: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
  isPrimary: z.boolean().optional(),
});

export type UpdateEntityContactRequest = z.infer<typeof updateEntityContactSchema>;
